/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueErrorSnackbar } from 'actions/Notifications';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/**
 * Search products by text query.
 * Numeric queries (#123 / 123) also trigger a resolve-by-ID fast-path on the
 * backend before falling back to full-text.
 */
export const searchProducts = createAsyncThunk(
  'productSelector/searchProducts',
  async ({ query, limit = 8 }, { extra, rejectWithValue }) => {
    try {
      const { productSelectorApi } = extra.serviceRegistry;
      if (!productSelectorApi) {
        return rejectWithValue('productSelectorApi not available on current transport');
      }
      return await productSelectorApi.searchProducts(query, limit);
    } catch (err) {
      return rejectWithValue(err?.message || 'search failed');
    }
  },
);

/**
 * Resolve saved product IDs to canonical product cards.
 * Returns { items, unresolved_ids }.
 */
export const resolveProducts = createAsyncThunk(
  'productSelector/resolveProducts',
  async (ids, { extra, dispatch, rejectWithValue }) => {
    try {
      if (!ids || ids.length === 0) {
        return { items: [], unresolved_ids: [] };
      }
      const { productSelectorApi } = extra.serviceRegistry;
      if (!productSelectorApi) {
        return rejectWithValue('productSelectorApi not available on current transport');
      }
      return await productSelectorApi.resolveProducts(ids);
    } catch (err) {
      dispatch(enqueueErrorSnackbar(
        err.message || getLocalizedMessage('productSelector.resolveFailed'),
        { variant: 'error' },
      ));
      return rejectWithValue(err?.message || 'resolve failed');
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const slice = createSlice({
  name: 'productSelector',
  initialState: {
    /** @type {Array} Full-text search results */
    searchResults: [],
    /** @type {'idle'|'pending'|'fulfilled'|'rejected'} */
    searchStatus: 'idle',

    /** @type {Array} Products resolved from saved IDs */
    resolvedProducts: [],
    /** @type {number[]} IDs that could not be resolved from the index */
    unresolvedIds: [],
    /** @type {'idle'|'pending'|'fulfilled'|'rejected'} */
    resolveStatus: 'idle',
  },
  reducers: {
    /** Clear search results when the search box is cleared. */
    clearProductSearchResults(state) {
      state.searchResults = [];
      state.searchStatus = 'idle';
    },
    /** Clear resolved products (e.g. when the saved IDs list is emptied). */
    clearResolvedProducts(state) {
      state.resolvedProducts = [];
      state.unresolvedIds = [];
      state.resolveStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchProducts.pending, (state) => {
        state.searchStatus = 'pending';
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.searchStatus = 'fulfilled';
        state.searchResults = action.payload || [];
      })
      .addCase(searchProducts.rejected, (state) => {
        state.searchStatus = 'rejected';
        state.searchResults = [];
      })

      .addCase(resolveProducts.pending, (state) => {
        state.resolveStatus = 'pending';
      })
      .addCase(resolveProducts.fulfilled, (state, action) => {
        state.resolveStatus = 'fulfilled';
        state.resolvedProducts = action.payload?.items || [];
        state.unresolvedIds = action.payload?.unresolved_ids || [];
      })
      .addCase(resolveProducts.rejected, (state) => {
        state.resolveStatus = 'rejected';
        state.resolvedProducts = [];
        state.unresolvedIds = [];
      });
  },
});

export const {
  clearProductSearchResults,
  clearResolvedProducts,
} = slice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectProductSearchResults = (state) => state.productSelector.searchResults;
export const selectProductSearchStatus   = (state) => state.productSelector.searchStatus;
export const selectResolvedProducts      = (state) => state.productSelector.resolvedProducts;
export const selectUnresolvedProductIds  = (state) => state.productSelector.unresolvedIds;
export const selectResolveStatus         = (state) => state.productSelector.resolveStatus;

export default slice.reducer;
